import React from 'react';

import { useIntl } from 'react-intl';

import { ExternalLink } from 'components/UI/ExternalLink';

import { seriesLabelKey } from './seriesMetadata';

export interface DeviceIdentityFields {
  sku: string;
  /** Already resolved for display — both callers translate `display_name` before passing it. */
  name: string;
  manufacturer: string | null;
  series?: DeviceSeries;
  imageUrl?: string;
  extendedRange?: boolean;
}

interface DeviceIdentityProps {
  device: DeviceIdentityFields;
  /**
   * When given, the picture and the name each link to the device's page.
   *
   * Opt-in, because only Rates and pricing links out (confirmed 2026-09-15). The ordering screen
   * shares this component and its card is already a button, so a link inside it would be a control
   * inside a control.
   */
  href?: string;
}

/**
 * How a device is presented wherever one is shown: picture, name, tier, manufacturer and SKU.
 *
 * Shared by the ordering screen's selectable card and the read-only price list on Rates & Pricing,
 * because those two must not drift — a device that reads *Direct+* while ordering and as a bare
 * SKU on the price list looks like two different products (Michel, 2026-09-15). Extracted rather
 * than reusing `DeviceCard`, which is a `role="button"` carrying selection, hover and pressed
 * state that a price list has no use for.
 *
 * Everything optional degrades rather than breaking: a priced SKU with no catalogue row behind it
 * has no picture and no tier, and still renders its name and number.
 */
export const DeviceIdentity = ({ device, href }: DeviceIdentityProps) => {
  const intl = useIntl();

  const picture = device.imageUrl ? (
    <img
      src={device.imageUrl}
      alt={device.name}
      width={68}
      height={60}
      className="flex-shrink-0 rounded bg-light object-fit-contain"
    />
  ) : null;

  /*
   * The picture and the name carry the link, rather than a separate line of text beside them —
   * which is how a shop does it, and what a reader reaches for first (Charles, 2026-09-15).
   *
   * Two links to one destination rather than one wrapping both: an anchor around the picture *and*
   * the whole identity block would put the tier badge, manufacturer and SKU inside the link too,
   * and a screen reader would announce all of it as the link's name.
   */
  return (
    <div className="d-flex align-items-start gap-3">
      {picture &&
        (href ? (
          <ExternalLink href={href} data-test={`device-image-link-${device.sku}`}>
            {picture}
          </ExternalLink>
        ) : (
          picture
        ))}
      <div>
        <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
          {href ? (
            <ExternalLink
              href={href}
              className="fw-medium"
              data-test={`device-name-link-${device.sku}`}
            >
              {device.name}
            </ExternalLink>
          ) : (
            <span className="fw-medium">{device.name}</span>
          )}
          {device.series && (
            <span className={`rounded small px-2 device-badge-${device.series}`}>
              {intl.formatMessage({ id: seriesLabelKey(device.series) })}
            </span>
          )}
          {device.extendedRange && (
            <span className="device-badge-extended-range">
              ↔ {intl.formatMessage({ id: 'extendedRangeKey' })}
            </span>
          )}
        </div>
        <div className="d-flex align-items-center gap-1 small text-secondary">
          {device.manufacturer && (
            <>
              <span>{device.manufacturer}</span>
              <span>·</span>
            </>
          )}
          <span>{device.sku}</span>
        </div>
      </div>
    </div>
  );
};
